import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { formatAwst } from '@/utils/perthTime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Q8-2 (director 13 Oct 2026): long admin actions run as background jobs; this card lists them with their progress rows.
export const AdminTasksCard = ({ token }) => {
  const [tasks, setTasks] = useState(null);
  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/tasks?limit=20`, { headers: { Authorization: `Bearer ${token}` } });
      setTasks(r.data.tasks);
    } catch (e) { setTasks([]); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const running = (tasks || []).some((t) => t.status === 'running');
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const tone = (s) => (s === 'completed' ? 'text-green-700' : s === 'failed' ? 'text-red-700' : 'text-amber-700');
  return (
    <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="admin-tasks-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="font-bold">Background jobs</h3>
          <p className="text-xs text-muted-foreground">Purges, backup rewrites and expired-backup deletions run in the background; each has a progress row here. Times in Perth time.</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={load} data-testid="admin-tasks-refresh">Refresh</Button>
      </div>
      {tasks === null ? <p className="text-sm">Loading…</p> : tasks.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-tasks-empty">No background jobs yet.</p> : (
        <table className="w-full text-xs" data-testid="admin-tasks-table">
          <thead><tr className="text-left text-muted-foreground"><th className="py-1">Job</th><th>Started</th><th>Status</th><th>Progress</th><th>Outcome</th></tr></thead>
          <tbody>
            {tasks.map((t) => {
              const pct = t.progress?.total ? Math.round((100 * t.progress.done) / t.progress.total) : null;
              return (
                <tr key={t.id} className="border-t" data-testid={`admin-task-${t.id.slice(0, 8)}`}>
                  <td className="py-1 pr-2">{t.label}<span className="block font-mono text-[10px] text-muted-foreground">{t.kind} · {t.id.slice(0, 8)}</span></td>
                  <td className="pr-2 whitespace-nowrap">{formatAwst(t.started_at)}</td>
                  <td className={`pr-2 font-semibold ${tone(t.status)}`}>{t.status}</td>
                  <td className="pr-2 min-w-[160px]">
                    {pct !== null && <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mb-1"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>}
                    <span className="text-muted-foreground">{t.progress?.done ?? 0}{t.progress?.total ? `/${t.progress.total}` : ''} · {t.progress?.note}</span>
                  </td>
                  <td className="font-mono text-[10px] max-w-[260px] truncate">{t.error ? t.error : t.result ? Object.entries(t.result).filter(([, v]) => typeof v !== 'object').map(([k, v]) => `${k}=${v}`).slice(0, 6).join(' · ') : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
